import { ModalWrapper } from '@hqo/react-components-library/dist/molecules/modals/wrapper';
import { PAYMENT_CONTENT_WIDTH } from 'shared/consts';
import styled from 'styled-components';

export const ContentContainer = styled.div`
  width: 100%;
  height: 100%;
`;

export const Container = styled.div<{ isFullscreen?: boolean }>`
  ${({ isFullscreen }) =>
    isFullscreen &&
    `
    width: 100vw;
    height: 100vh;
  `}
`;

/*
 * Desktop presentation for a step that owns the screen.
 *
 * The receipt route brings its own modal via ReceiptContent, so it overlays whatever host it was
 * mounted in. The add-on step brought none and rendered inline — under the booking questions form
 * on `additional-information`, beside the booking form on the resource page. Mobile never showed it
 * because SwipeModal is already an overlay.
 *
 * Same width as the receipt: consecutive screens that change size read as different surfaces.
 */
export const StepModal = styled(ModalWrapper)`
  width: ${PAYMENT_CONTENT_WIDTH};

  .modal-background {
    backdrop-filter: none;
    background-color: ${({ theme }) => `${theme.colorsRgba.black_5}`};
  }

  /*
   * Arrow only to the eye, still named to a screen reader: the label is the control's ONLY accessible
   * name, so it is clipped, not removed.
   *
   * On StepModal, not StepContent — the control is rendered BESIDE the content, not inside it, so a
   * rule scoped to the content can never reach it.
   */
  .back-button-container div {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
  }

  /*
   * ⛔ THE WRAPPER IS A FLEX ROW BY DEFAULT, measured in the browser: left it alone and the header
   * element became a 167px-wide column down the left side with the title wrapped onto two lines,
   * rather than a band above the content. A column is what a header and a body want.
   *
   * (No backticks in here: this is inside a template literal, and one would close it.)
   */
  .modal-wrapper {
    flex-direction: column;
  }

  /*
   * The header band the step's title sits in, between the two absolutely-positioned corner controls —
   * so the title is centred against the modal while the back arrow and the exit X sit over its left
   * and right ends rather than pushing it off-centre.
   */
  .header-element {
    flex: 0 0 auto;
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 56px;
    padding: 0 56px;
    box-sizing: border-box;
  }

  @media only screen and (max-width: ${PAYMENT_CONTENT_WIDTH}) {
    width: 100%;

    /*
     * The link between the shell and the step. The wrapper is a flex column (see above) and the
     * library gives it the full height; without this the content box stays auto and every definite
     * height below it resolves to auto too, which is what left the CTA unpinned.
     */
    .modal-wrapper-content {
      flex: 1 1 auto;
      min-height: 0;
      display: flex;
      flex-direction: column;
    }

    .modal-wrapper-exit-button {
      z-index: 15;
    }
  }
`;

/*
 * ⛔ ModalWrapper does NOT render `title` — it passes it to the dialog's `aria-label` and nothing else.
 * So the step's title has to be handed over as a `headerElement`, and `title` is still worth passing
 * alongside it because it is what names the dialog to a screen reader.
 */
export const StepTitle = styled.h2`
  margin: 0;
  font: 600 18px/24px ${({ theme }) => theme.fonts.join()};
  color: ${({ theme }) => theme.colors.fontColor};
  text-align: center;
`;

/*
 * Sizes to its CONTENT, which is why this exists rather than reusing ModalContainer.
 *
 * That one is a fixed 528px here and CtaBar pins itself with margin-top:auto, so a one-vendor menu
 * left a dead band above the CTA. Height is capped, not fixed. Not a change to ModalContainer: it is
 * shared with the payment-iframe flows, whose layout depends on that size.
 */
export const StepContent = styled.div`
  width: 100%;
  max-height: 70vh;
  overflow-y: auto;
  overflow-x: hidden;

  /*
   * The receipt modal's frame, so two screens in one flow share a box. Set as the page's GUTTER
   * rather than padding here, which would stack on PageContainer's own and double the inset.
   */
  /*
   * 68px once, to clear the library's back control — absolute at 30px/54px, and it overprinted the
   * step's title at 24. The header band above now occupies that space and holds the control, so the
   * clearance is paid for twice: measured on the preview, 80px of air between the header and the
   * first card. Back to the receipt's own 24.
   */
  --step-gutter-y: 24px;
  --step-gutter-x: 56px;

  /*
   * ⛔ BELOW THE BREAKPOINT THIS IS NOT A DIALOG, so it must not be sized like one.
   *
   * Above it the modal is a centred card: it hugs its content and 70vh only stops a long menu
   * outgrowing the screen. Below it the modal is a full-width panel filling the shell, and a cap at
   * 70vh leaves the rest of that shell empty — measured on the preview at 760x736, 211px of nothing
   * under a CTA floating mid-screen. Worse at 760x536, where the cap lands above the CTA and pushes
   * it out of the scrollport: the tenant has to scroll to reach Continue.
   *
   * So here it fills instead, and PageContainer's existing margin-top:auto on the CTA bar — inert
   * until now, because height:100% against a max-height-only parent resolves to auto — finally
   * has a definite box to push against. Height comes from the shell, not from a share of the viewport.
   */
  @media only screen and (max-width: ${PAYMENT_CONTENT_WIDTH}) {
    --step-gutter-x: 16px;

    max-height: none;
    flex: 1 1 auto;
    min-height: 0;
    display: flex;
    flex-direction: column;
    /* PageContainer owns the scroll here; two nested scrollports is the state RES-1326 avoided. */
    overflow: hidden;

    /* The page itself, given the same treatment so the chain stays definite all the way down. */
    > * {
      flex: 1 1 auto;
      min-height: 0;
    }
  }
`;
